/**
 * Renderer - Handles all drawing operations with isometric projection
 */

import { Vector2 } from '../math/Vector2';
import { Vector3 } from '../math/Vector3';
import { Rectangle } from '../math/Rectangle';

// Isometric tile dimensions
export const TILE_WIDTH = 64;
export const TILE_HEIGHT = 32;

export class Renderer {
  private ctx: CanvasRenderingContext2D;
  private canvas: HTMLCanvasElement;
  private cameraOffset: Vector2 = new Vector2(0, 0);
  private zoom: number = 1;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context');
    }
    this.ctx = ctx;
  }

  /** Convert world coordinates to screen coordinates (isometric projection) */
  worldToScreen(worldPos: Vector3): Vector2 {
    const screenX = (worldPos.x - worldPos.y) * (TILE_WIDTH / 2) * this.zoom;
    const screenY = (worldPos.x + worldPos.y) * (TILE_HEIGHT / 2) * this.zoom - worldPos.z * this.zoom;
    return new Vector2(
      screenX + this.canvas.width / 2 - this.cameraOffset.x * this.zoom,
      screenY + this.canvas.height / 2 - this.cameraOffset.y * this.zoom
    );
  }

  /** Convert screen coordinates to world coordinates */
  screenToWorld(screenPos: Vector2): Vector3 {
    const adjustedX = (screenPos.x - this.canvas.width / 2 + this.cameraOffset.x * this.zoom) / this.zoom;
    const adjustedY = (screenPos.y - this.canvas.height / 2 + this.cameraOffset.y * this.zoom) / this.zoom;

    const worldX = (adjustedX / (TILE_WIDTH / 2) + adjustedY / (TILE_HEIGHT / 2)) / 2;
    const worldY = (adjustedY / (TILE_HEIGHT / 2) - adjustedX / (TILE_WIDTH / 2)) / 2;

    return new Vector3(worldX, worldY, 0);
  }

  setCameraOffset(offset: Vector2): void {
    this.cameraOffset = offset;
  }

  getCameraOffset(): Vector2 {
    return this.cameraOffset;
  }

  setZoom(zoom: number): void {
    this.zoom = Math.max(0.5, Math.min(2, zoom));
  }

  getZoom(): number {
    return this.zoom;
  }

  clear(color: string = '#1a1a2e'): void {
    const w = this.canvas.width;
    const h = this.canvas.height;

    // Reset canvas completely by reassigning width
    this.canvas.width = w;
    this.ctx = this.canvas.getContext('2d')!;

    // Fill with background color
    this.ctx.fillStyle = color;
    this.ctx.fillRect(0, 0, w, h);
  }

  /** Draw an isometric tile (diamond shape) */
  drawIsometricTile(worldPos: Vector3, color: string, strokeColor: string = '#000'): void {
    const screenPos = this.worldToScreen(worldPos);
    const hw = (TILE_WIDTH / 2) * this.zoom;
    const hh = (TILE_HEIGHT / 2) * this.zoom;

    this.ctx.beginPath();
    this.ctx.moveTo(screenPos.x, screenPos.y - hh);
    this.ctx.lineTo(screenPos.x + hw, screenPos.y);
    this.ctx.lineTo(screenPos.x, screenPos.y + hh);
    this.ctx.lineTo(screenPos.x - hw, screenPos.y);
    this.ctx.closePath();

    this.ctx.fillStyle = color;
    this.ctx.fill();
    this.ctx.strokeStyle = strokeColor;
    this.ctx.lineWidth = 1;
    this.ctx.stroke();
  }

  /** Draw an isometric cube/block */
  drawIsometricBlock(worldPos: Vector3, height: number, topColor: string, leftColor: string, rightColor: string): void {
    const screenPos = this.worldToScreen(worldPos);
    const hw = (TILE_WIDTH / 2) * this.zoom;
    const hh = (TILE_HEIGHT / 2) * this.zoom;
    const h = height * this.zoom;

    // Top face
    this.ctx.beginPath();
    this.ctx.moveTo(screenPos.x, screenPos.y - hh - h);
    this.ctx.lineTo(screenPos.x + hw, screenPos.y - h);
    this.ctx.lineTo(screenPos.x, screenPos.y + hh - h);
    this.ctx.lineTo(screenPos.x - hw, screenPos.y - h);
    this.ctx.closePath();
    this.ctx.fillStyle = topColor;
    this.ctx.fill();
    this.ctx.strokeStyle = '#000';
    this.ctx.lineWidth = 1;
    this.ctx.stroke();

    // Left face
    this.ctx.beginPath();
    this.ctx.moveTo(screenPos.x - hw, screenPos.y - h);
    this.ctx.lineTo(screenPos.x, screenPos.y + hh - h);
    this.ctx.lineTo(screenPos.x, screenPos.y + hh);
    this.ctx.lineTo(screenPos.x - hw, screenPos.y);
    this.ctx.closePath();
    this.ctx.fillStyle = leftColor;
    this.ctx.fill();
    this.ctx.strokeStyle = '#000';
    this.ctx.lineWidth = 1;
    this.ctx.stroke();

    // Right face
    this.ctx.beginPath();
    this.ctx.moveTo(screenPos.x + hw, screenPos.y - h);
    this.ctx.lineTo(screenPos.x, screenPos.y + hh - h);
    this.ctx.lineTo(screenPos.x, screenPos.y + hh);
    this.ctx.lineTo(screenPos.x + hw, screenPos.y);
    this.ctx.closePath();
    this.ctx.fillStyle = rightColor;
    this.ctx.fill();
    this.ctx.strokeStyle = '#000';
    this.ctx.lineWidth = 1;
    this.ctx.stroke();
  }

  /** Draw a sprite/image at world position */
  drawSprite(
    image: HTMLImageElement,
    worldPos: Vector3,
    srcRect?: Rectangle,
    width?: number,
    height?: number
  ): void {
    const screenPos = this.worldToScreen(worldPos);
    const w = (width ?? image.width) * this.zoom;
    const h = (height ?? image.height) * this.zoom;

    if (srcRect) {
      this.ctx.drawImage(
        image,
        srcRect.x, srcRect.y, srcRect.width, srcRect.height,
        screenPos.x - w / 2, screenPos.y - h, w, h
      );
    } else {
      this.ctx.drawImage(image, screenPos.x - w / 2, screenPos.y - h, w, h);
    }
  }

  /** Draw a colored rectangle at world position */
  drawRect(worldPos: Vector3, width: number, height: number, color: string): void {
    const screenPos = this.worldToScreen(worldPos);
    const w = width * this.zoom;
    const h = height * this.zoom;
    this.ctx.fillStyle = color;
    this.ctx.fillRect(screenPos.x - w / 2, screenPos.y - h, w, h);
  }

  /** Draw a circle at world position */
  drawCircle(worldPos: Vector3, radius: number, color: string, filled: boolean = true): void {
    const screenPos = this.worldToScreen(worldPos);
    const r = radius * this.zoom;
    this.ctx.beginPath();
    this.ctx.arc(screenPos.x, screenPos.y, r, 0, Math.PI * 2);
    this.ctx.closePath();
    if (filled) {
      this.ctx.fillStyle = color;
      this.ctx.fill();
    } else {
      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = 2;
      this.ctx.stroke();
    }
  }

  /** Draw a line between two world positions */
  drawLine(startPos: Vector3, endPos: Vector3, color: string, lineWidth: number = 2): void {
    const screenStart = this.worldToScreen(startPos);
    const screenEnd = this.worldToScreen(endPos);

    this.ctx.beginPath();
    this.ctx.moveTo(screenStart.x, screenStart.y);
    this.ctx.lineTo(screenEnd.x, screenEnd.y);
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = lineWidth * this.zoom;
    this.ctx.lineCap = 'round';
    this.ctx.stroke();

    // Reset line properties to defaults to prevent affecting subsequent draw calls
    this.ctx.lineWidth = 1;
    this.ctx.lineCap = 'butt';
  }

  /** Draw text at screen position */
  drawText(
    text: string,
    x: number,
    y: number,
    color: string = '#fff',
    font: string = '14px monospace',
    align: CanvasTextAlign = 'left'
  ): void {
    this.ctx.fillStyle = color;
    this.ctx.font = font;
    this.ctx.textAlign = align;
    this.ctx.fillText(text, x, y);
  }

  /** Draw text at world position */
  drawTextWorld(
    text: string,
    worldPos: Vector3,
    color: string = '#fff',
    font: string = '14px monospace'
  ): void {
    const screenPos = this.worldToScreen(worldPos);
    this.drawText(text, screenPos.x, screenPos.y, color, font, 'center');
  }

  /** Draw a health bar */
  drawHealthBar(worldPos: Vector3, current: number, max: number, width: number = 40, height: number = 6): void {
    const screenPos = this.worldToScreen(worldPos);
    const w = width * this.zoom;
    const h = height * this.zoom;
    const ratio = current / max;

    // Clear any existing path to prevent interference
    this.ctx.beginPath();

    // Background
    this.ctx.fillStyle = '#333';
    this.ctx.fillRect(screenPos.x - w / 2, screenPos.y - 30 * this.zoom, w, h);

    // Health
    const healthColor = ratio > 0.5 ? '#4ecca3' : ratio > 0.25 ? '#f39c12' : '#e74c3c';
    this.ctx.fillStyle = healthColor;
    this.ctx.fillRect(screenPos.x - w / 2, screenPos.y - 30 * this.zoom, w * ratio, h);

    // Border
    this.ctx.strokeStyle = '#000';
    this.ctx.lineWidth = 1;
    this.ctx.strokeRect(screenPos.x - w / 2, screenPos.y - 30 * this.zoom, w, h);
  }

  /** Draw UI rectangle (screen space) */
  drawUIRect(x: number, y: number, width: number, height: number, color: string, borderColor?: string): void {
    this.ctx.fillStyle = color;
    this.ctx.fillRect(x, y, width, height);
    if (borderColor) {
      this.ctx.strokeStyle = borderColor;
      this.ctx.lineWidth = 2;
      this.ctx.strokeRect(x, y, width, height);
    }
  }

  /** Apply darkness overlay for night with optional light sources */
  applyDarknessOverlay(intensity: number, lightSources?: { worldPos: Vector3; radius: number; color?: string }[]): void {
    if (intensity >= 1) return;

    const darkness = 1 - intensity;

    // If no light sources or full daylight, just apply simple overlay
    if (!lightSources || lightSources.length === 0) {
      this.ctx.fillStyle = `rgba(0, 0, 30, ${darkness})`;
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      return;
    }

    // Use an off-screen canvas for the darkness with light cutouts
    const offCanvas = document.createElement('canvas');
    offCanvas.width = this.canvas.width;
    offCanvas.height = this.canvas.height;
    const offCtx = offCanvas.getContext('2d')!;

    // Fill with darkness
    offCtx.fillStyle = `rgba(0, 0, 30, ${darkness})`;
    offCtx.fillRect(0, 0, offCanvas.width, offCanvas.height);

    // Cut out light areas using destination-out compositing
    offCtx.globalCompositeOperation = 'destination-out';

    for (const light of lightSources) {
      const screenPos = this.worldToScreen(light.worldPos);
      const screenRadius = light.radius * TILE_WIDTH * this.zoom;

      // Create radial gradient for soft light edge
      const gradient = offCtx.createRadialGradient(
        screenPos.x, screenPos.y, 0,
        screenPos.x, screenPos.y, screenRadius
      );
      gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');   // Full cutout at center
      gradient.addColorStop(0.5, 'rgba(0, 0, 0, 0.7)');
      gradient.addColorStop(0.8, 'rgba(0, 0, 0, 0.3)');
      gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');   // No cutout at edge

      offCtx.fillStyle = gradient;
      offCtx.beginPath();
      offCtx.arc(screenPos.x, screenPos.y, screenRadius, 0, Math.PI * 2);
      offCtx.fill();
    }

    // Draw the darkness overlay with cutouts to main canvas
    this.ctx.drawImage(offCanvas, 0, 0);

    // Add warm glow for each light source (on top of everything)
    for (const light of lightSources) {
      const screenPos = this.worldToScreen(light.worldPos);
      const screenRadius = light.radius * TILE_WIDTH * this.zoom * 0.5;
      const glowColor = light.color ?? 'rgba(255, 150, 50, 0.15)';

      const glowGradient = this.ctx.createRadialGradient(
        screenPos.x, screenPos.y, 0,
        screenPos.x, screenPos.y, screenRadius
      );
      glowGradient.addColorStop(0, glowColor);
      glowGradient.addColorStop(1, 'rgba(255, 150, 50, 0)');

      this.ctx.fillStyle = glowGradient;
      this.ctx.beginPath();
      this.ctx.arc(screenPos.x, screenPos.y, screenRadius, 0, Math.PI * 2);
      this.ctx.fill();
    }
  }

  getContext(): CanvasRenderingContext2D {
    return this.ctx;
  }

  getCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  getWidth(): number {
    return this.canvas.width;
  }

  getHeight(): number {
    return this.canvas.height;
  }
}
